import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useBackgroundJobStatus } from '~/modules/common/hooks/useBackgroundJobStatus';
import { BACKGROUND_JOB_STATUS } from '~/modules/common/enums/BackgroundJobStatus';
import { useSnackbar } from '~/modules/common/snackbar';
import { useResourceUserTaskAssignmentBulkAction } from './useResourceUserTaskAssignmentBulkAction';

export const useBulkAssignTasksSubmit = ({
  projectId,
  resource,
  textSearch,
  resetTaskAssignments,
  onTaskAssignmentUpdateSuccess,
  onClose
}) => {
  const { showSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const [jobId, setJobId] = useState(null);
  const { status, hasProcessed } = useBackgroundJobStatus({
    jobId
  });

  const {
    isBulkActionInProgress,
    handleBulkAssignTasksSubmit
  } = useResourceUserTaskAssignmentBulkAction({
    projectId,
    resource,
    textSearch
  });

  const handleJobSuccess = useCallback(() => {
    onTaskAssignmentUpdateSuccess({ userId: resource.id });

    showSnackbar &&
      showSnackbar(
        formatMessage({
          id:
            'taskAssignmentBulkActionDialog.assignAllTasksSnackbarSuccessMessage'
        })
      );

    onClose();
    resetTaskAssignments();
  }, [
    resource.id,
    onTaskAssignmentUpdateSuccess,
    onClose,
    resetTaskAssignments,
    showSnackbar,
    formatMessage
  ]);

  useEffect(() => {
    if (jobId && hasProcessed && status === BACKGROUND_JOB_STATUS.SUCCEEDED) {
      handleJobSuccess();
    }
  }, [jobId, hasProcessed, status, handleJobSuccess]);

  const handleSubmit = useCallback(async () => {
    const newJobId = await handleBulkAssignTasksSubmit();

    setJobId(newJobId);
  }, [handleBulkAssignTasksSubmit]);

  return {
    isBulkAssignTasksInProgress: Boolean(
      isBulkActionInProgress || (jobId && !hasProcessed)
    ),
    handleBulkAssignTasksSubmit: handleSubmit
  };
};
